<template>
  <div class="product-list">
    <!-- 商品档案（企业微信手机端，仅查看） -->
    <mobile-list-header>
      <!-- 第一行：搜索框占满整行，左输入框 + 右搜索按钮 -->
      <div class="search-bar">
        <div class="search-input">
          <yq-icon class="icon" name="element-ui_search"/>
          <input v-model="searchInfo.searchKey" placeholder="名称/编码/拼音码" @keyup.enter="resetSearch"/>
          <yq-icon v-if="searchInfo.searchKey" class="icon clear" name="element-ui_close" @click="clearKey"/>
        </div>
        <div class="search-btn" @click="resetSearch">搜索</div>
      </div>
      <!-- 第二行：查询条件，无背景色，超过一屏横向滑动 -->
      <div class="filter-row">
        <mobile-condition-select v-model="searchInfo.category" :data="categoryTree"
                                 label-column="dictName" code-column="dictCode" type="tree"
                                 placeholder="商品分类" @change="resetSearch"/>
        <mobile-condition-select v-model="searchInfo.searchType" :data="productTypeList"
                                 label-column="name" code-column="code" type="list"
                                 placeholder="商品类型" @change="resetSearch"/>
        <mobile-condition-select v-model="searchInfo.bankup1" :data="productLineList"
                                 label-column="dictName" code-column="dictCode" type="list"
                                 placeholder="产品线" @change="resetSearch"/>
        <mobile-condition-select v-model="searchInfo.status" :data="statusOptions"
                                 label-column="label" code-column="value" type="list"
                                 placeholder="状态" @change="resetSearch"/>
      </div>
    </mobile-list-header>

    <mobile-list :loading="loading" :has-more="searchInfo.hasMore" :query-list="queryList"
                 :refresh="resetSearch" ref="mobileList">
      <list-card class="product-item" v-for="item in dataList" :key="item.id" :label="item.productName">
        <template #desc>
          <span class="type-tag">{{ typeName(item.productType) }}</span>
          <span class="status-tag" :class="Number(item.status) === 1 ? 'on' : 'off'">
            {{ Number(item.status) === 1 ? "上架" : "下架" }}
          </span>
        </template>
        <data-column label="商品编码">{{ item.productCodeBase || "—" }}</data-column>
        <data-column label="规格型号">{{ item.spec || "—" }}</data-column>
        <data-column label="商品分类">{{ categoryName(item.categoryId) || "—" }}</data-column>
        <data-column label="产品线">{{ productLineName(item.bankup1) || "—" }}</data-column>
        <data-column label="毛利">{{ grossProfitRate(item) === null ? "—" : grossProfitRate(item) + "%" }}</data-column>
        <data-column label="累计销量">{{ formatNumber(item.totalSales) }}</data-column>
      </list-card>
    </mobile-list>
  </div>
</template>

<script>

import {useRoute, useRouter} from "vue-router";
import MobileList from "@/components/qywx/layout/MobileList.vue";
import MobileListHeader from "@/components/qywx/layout/MobileListHeader.vue";
import MobileConditionSelect from "@/components/qywx/data/MobileConditionSelect.vue";
import ListCard from "@/components/qywx/layout/ListCard.vue";
import DataColumn from "@/components/qywx/data/DataColumn.vue";
import YqIcon from "@/components/YqIcon.vue";
import productApi from "@/api/normal/product/productApi";
import commonApi from "@/api/commonApi";
import util from "@/util/util";

const STATUS_OPTIONS = [
  {value: 1, label: "上架"},
  {value: 0, label: "下架"},
];

export default {
  setup() {
    return {
      router: useRouter(),
      route: useRoute(),
    }
  },
  name: "ProductListQW",
  components: {
    YqIcon, DataColumn, ListCard, MobileConditionSelect, MobileListHeader, MobileList
  },
  data() {
    return {
      loading: false,
      searchInfo: {
        searchKey: "",
        category: "",
        searchType: "",
        bankup1: "",
        status: "",
        pageNumber: 0,
        pageSize: 20,
        hasMore: true, // 初始为 true，列表挂载后由无限滚动自动触发首次查询
      },
      dataList: [],
      categoryTree: [],
      productTypeList: [],
      productLineList: [],
      statusOptions: STATUS_OPTIONS,
    };
  },
  created() {
    // 商品分类树 / 产品线（普通字典 CRM_PRODUCT_LINE）/ 商品类型（系统配置）
    commonApi.queryDictionaryTree("PRODUCT_CATEGORY").then(list => this.categoryTree = list || []);
    commonApi.queryDictionaryList("CRM_PRODUCT_LINE").then(list => this.productLineList = list || []);
    util.getSystemSettingOptions("PRODUCT_STOCK_TYPE").then(list => this.productTypeList = list || []);
  },
  methods: {
    queryList() {
      this.loading = true;
      this.searchInfo.pageNumber++;
      productApi.queryList({
        ...this.cleanParams(),
        pageNumber: this.searchInfo.pageNumber,
        pageSize: this.searchInfo.pageSize,
      }).then(res => {
        this.dataList = this.dataList.concat(res.records || []);
        this.searchInfo.hasMore = res.pageCount > this.searchInfo.pageNumber;
        this.loading = false;
        this.$refs.mobileList.hideRefreshTitle();
      }).catch(() => {
        this.loading = false;
      })
    },
    resetSearch() {
      this.searchInfo.pageNumber = 0;
      this.dataList = [];
      this.searchInfo.hasMore = true;
      this.queryList();
    },
    clearKey() {
      this.searchInfo.searchKey = "";
      this.resetSearch();
    },
    cleanParams() {
      const params = {};
      ["searchKey", "category", "searchType", "bankup1", "status"].forEach(key => {
        const value = this.searchInfo[key];
        if (value !== "" && value !== null && value !== undefined) {
          params[key] = value;
        }
      });
      return params;
    },
    // 商品类型：系统配置返回 {code, name}
    typeName(type) {
      const item = this.productTypeList.find(t => t.code === type);
      return item ? item.name : (type || "—");
    },
    // 产品线是普通字典（值存 ba_product.bankup1），按 dictCode 匹配
    productLineName(code) {
      const item = this.productLineList.find(d => d.dictCode === code);
      return item ? item.dictName : "";
    },
    // 分类树按 dictCode 查找名称（顶层 + 子级）
    categoryName(code) {
      return this.findTreeName(this.categoryTree, code);
    },
    findTreeName(list, code) {
      for (let i = 0; i < list.length; i++) {
        if (list[i].dictCode === code) {
          return list[i].dictName;
        }
        const children = list[i].children;
        if (children && children.length > 0) {
          const name = this.findTreeName(children, code);
          if (name) {
            return name;
          }
        }
      }
      return "";
    },
    // 毛利(纯前端展示) = (销售价格 - 成本价格) / 销售价格，保留 2 位；销售价为空/0 时返回 null
    grossProfitRate(row) {
      const sale = Number(row.salePriceBase);
      const buy = Number(row.buyPriceBase);
      if (!sale || isNaN(sale) || isNaN(buy)) return null;
      return Math.round(((sale - buy) / sale) * 10000) / 100;
    },
    formatNumber(value) {
      if (value === null || value === undefined || value === "") {
        return "—";
      }
      const n = Number(value);
      return isNaN(n) ? "—" : n.toLocaleString("zh-CN", {maximumFractionDigits: 2});
    },
  }
};
</script>

<style lang="scss" scoped>
.product-list{
  height: 100%;
  display: flex;
  flex-direction: column;
}
// 第一行：搜索框占满整行，左输入框 + 右搜索按钮
.search-bar{
  display: flex;
  align-items: center;
  height: 44px;
  padding-left: 12px;
  border-radius: 999px;
  background: var(--yq-background-color);
  overflow: hidden;
  .search-input{
    flex: 1;
    display: flex;
    align-items: center;
    min-width: 0;
    height: 100%;
    padding-right: 12px;
    .icon{
      flex: none;
      color: var(--yq-font-color-desc);
      font-size: var(--yq-font-size-mini-mobile);
      &.clear{
        margin-left: 6px;
      }
    }
    input{
      flex: 1;
      min-width: 0;
      height: 100%;
      margin-left: 6px;
      border: none;
      outline: none;
      background: transparent;
      font-size: var(--yq-font-size-mini-mobile);
      color: var(--yq-font-color-base);
      &::placeholder{
        color: var(--yq-font-color-desc);
      }
    }
  }
  .search-btn{
    flex: none;
    display: flex;
    align-items: center;
    height: 100%;
    padding: 0 18px;
    background: var(--el-color-primary);
    color: #FFF;
    font-size: var(--yq-font-size-mini-mobile);
  }
}
// 第二行：查询条件，无背景色，超过一屏横向滑动
.filter-row{
  display: flex;
  gap: 18px;
  margin-top: 10px;
  overflow-x: auto;
  scrollbar-width: none;
  &::-webkit-scrollbar{
    display: none;
  }
  > *{
    flex: none;
  }
}
.product-item{
  :deep(.card-title .desc){
    display: flex;
    align-items: center;
    gap: 6px;
  }
  .type-tag,
  .status-tag{
    flex: none;
    padding: 2px 8px;
    border-radius: 4px;
    font-size: var(--yq-font-size-mini-mobile);
  }
  .type-tag{
    color: var(--el-color-primary);
    background: var(--el-color-primary-light-9);
  }
  .status-tag{
    &.on{
      color: var(--el-color-success);
      background: var(--el-color-success-light-9);
    }
    &.off{
      color: var(--el-color-info);
      background: var(--el-fill-color-light);
    }
  }
}
</style>
